'use client'

import Link from 'next/link'
import React from 'react'

import { HeroChatLauncherSlot } from '@/components/Hero/HeroChatLauncherSlot'
import { PageHero } from '@/components/Hero/PageHero'
import { HeroMediaOverlay } from '@/components/Hero/HeroMediaOverlay'
import {
  PAGE_HERO_FULL_VIEWPORT_CLASS_NAME,
  pageHeroVideoControlClassName,
} from '@/components/Hero/pageHeroLayout'
import { useHeroStickyBannerClearance } from '@/components/Hero/useHeroStickyBannerClearance'
import {
  HeroVideoLayer,
  HeroVideoPlaybackButton,
  useHeroVideoBackground,
} from '@/components/Hero/HeroVideoBackground'
import { Button } from '@/components/ui/button'
import { useMessages } from '@/lib/i18n/useMessages'
import { useLocale } from '@/lib/i18n/useLocale'
import { localizePath } from '@/lib/i18n/config'
import { getInspectionCalendarHref } from '@/lib/pages/inspectionCalendarLink'
import type { City } from '@/lib/mock/cities'
import { cn } from '@/utilities/ui'

type CityHeroProps = {
  city: City
}

export const CityHero: React.FC<CityHeroProps> = ({ city }) => {
  const messages = useMessages()
  const locale = useLocale()

  const heroVideo = useHeroVideoBackground({
    desktopSrc: city.heroVideoDesktop,
    mobileSrc: city.heroVideoMobile,
    poster: city.heroImage,
    posterAlt: city.heroImageAlt,
  })
  const { compact, contentClassName } = useHeroStickyBannerClearance('city-full-viewport')

  return (
    <PageHero
      aria-label={`${city.name} overview`}
      as="section"
      className={PAGE_HERO_FULL_VIEWPORT_CLASS_NAME}
      background={
        <>
          <HeroVideoLayer video={heroVideo} />
          <HeroMediaOverlay tone="city" />
        </>
      }
      contentClassName={contentClassName}
      contentWrapperClassName={cn(
        'mx-auto max-w-[1680px] px-4',
        'relative flex w-full flex-1 flex-col',
      )}
      gradientClassName={false}
      heightMode="viewport"
      pullUnderHeader={false}
    >
      <HeroVideoPlaybackButton className={pageHeroVideoControlClassName} video={heroVideo} />

      <HeroChatLauncherSlot active={!compact} />

      <h1 className="max-w-full font-display text-[3rem] font-semibold leading-tight tracking-[-0.04em] text-white drop-shadow-[0_16px_32px_-8px_rgba(12,12,13,0.4)] md:text-[4rem]">
        {city.name}
      </h1>

      <div className="flex flex-wrap gap-3">
        <Button asChild>
          <Link href={getInspectionCalendarHref()} rel="noopener noreferrer" target="_blank">
            {messages.cityPage.primaryCta}
            <span className="sr-only"> {messages.common.opensInNewTab}</span>
          </Link>
        </Button>
        <Button asChild>
          <Link href={localizePath('/apartments', locale)}>{messages.cityPage.secondaryCta}</Link>
        </Button>
      </div>
    </PageHero>
  )
}
